import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { TOPICS_CURRICULUM } from "@/lib/constants";
import { Progress } from "@/components/ui/progress";
import { BookMarked, Loader2, Star, CheckCircle2 } from "lucide-react";

const STATUS_META = {
  good: { label: "İyi Olduğu Konu", chip: "bg-amber-400 border-amber-400 text-white hover:bg-amber-500" },
  done: { label: "Bitirdiği Konu", chip: "bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600" },
};
const NEXT = { undefined: "good", good: "done", done: "" };
const AYT_ALANLAR = ["Sayısal", "Eşit Ağırlık", "Sözel"];

const examOf = (track) => (track || "").startsWith("AYT") ? "AYT" : "TYT";
const alanOf = (track) => {
  const t = track || "";
  if (t.includes("Sayısal")) return "Sayısal";
  if (t.includes("Eşit")) return "Eşit Ağırlık";
  if (t.includes("Sözel")) return "Sözel";
  return "Sayısal";
};

export default function TopicTracker({ studentId, readOnly, track }) {
  const [exam, setExam] = useState(() => examOf(track));
  const [alan, setAlan] = useState(() => alanOf(track));
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const scope = exam === "TYT" ? "TYT" : `AYT ${alan}`;
  const k = (subject, topic) => `${scope}|${subject}|${topic}`;

  const curriculum = useMemo(
    () => (exam === "TYT" ? TOPICS_CURRICULUM.TYT : TOPICS_CURRICULUM.AYT[alan] || {}),
    [exam, alan]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/topic-progress/${studentId}`);
      const map = {};
      data.forEach((d) => { map[`${d.exam}|${d.subject}|${d.topic}`] = d.status; });
      setStatuses(map);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { if (studentId) load(); }, [load, studentId]);

  const toggle = async (subject, topic) => {
    if (readOnly) return;
    const key = k(subject, topic);
    const cur = statuses[key];
    const next = NEXT[String(cur)] ?? "";
    setBusyKey(key);
    setStatuses((p) => {
      const m = { ...p };
      if (next) m[key] = next; else delete m[key];
      return m;
    });
    try {
      await api.put(`/topic-progress/${studentId}`, { exam: scope, subject, topic, status: next });
    } catch (e) {
      toast.error(apiErr(e));
      load();
    } finally {
      setBusyKey(null);
    }
  };

  const stats = useMemo(() => {
    let total = 0, good = 0, done = 0;
    Object.entries(curriculum).forEach(([subj, topics]) =>
      topics.forEach((t) => {
        total++;
        const s = statuses[k(subj, t)];
        if (s === "good") good++;
        if (s === "done") done++;
      })
    );
    return { total, good, done, marked: good + done };
  }, [curriculum, statuses, scope]);

  return (
    <div className="space-y-5" data-testid="topic-tracker">
      <div>
        <h3 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><BookMarked size={20} className="text-indigo-600" /> Konu Takibi</h3>
        <p className="text-sm text-slate-500 mt-0.5">
          {readOnly
            ? "Koçunuzun işaretlediği konu durumları: yeşil bitirilen, turuncu iyi olunan konu."
            : "Konuya tıklayarak durumu değiştirin: 1. tık iyi (turuncu), 2. tık bitirdi (yeşil), 3. tık temizler."}
        </p>
      </div>

      <div className="card-ta p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="topic-exam-toggle">
            {["TYT", "AYT"].map((e) => (
              <button key={e} onClick={() => setExam(e)} data-testid={`topic-exam-${e}`}
                className={`px-4 py-1.5 text-sm font-semibold transition-colors ${exam === e ? "bg-indigo-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                {e}
              </button>
            ))}
          </div>
          {exam === "AYT" && (
            <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="topic-alan-toggle">
              {AYT_ALANLAR.map((a) => (
                <button key={a} onClick={() => setAlan(a)} data-testid={`topic-alan-${a}`}
                  className={`px-3.5 py-1.5 text-sm font-semibold transition-colors ${alan === a ? "bg-violet-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                  {a}
                </button>
              ))}
            </div>
          )}
          <div className="ml-auto flex items-center gap-3 text-xs font-semibold">
            <span className="flex items-center gap-1.5 text-amber-600"><span className="w-3 h-3 rounded bg-amber-400 inline-block" /> İyi: {stats.good}</span>
            <span className="flex items-center gap-1.5 text-emerald-600"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> Bitirdi: {stats.done}</span>
            <span className="text-slate-400">Toplam: {stats.total} konu</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Progress value={stats.total ? Math.round((stats.marked / stats.total) * 100) : 0} className="h-2 flex-1" />
          <span className="text-xs font-mono text-slate-600" data-testid="topic-progress-pct">%{stats.total ? Math.round((stats.marked / stats.total) * 100) : 0}</span>
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(curriculum).map(([subject, topics]) => {
            const doneN = topics.filter((t) => statuses[k(subject, t)] === "done").length;
            const goodN = topics.filter((t) => statuses[k(subject, t)] === "good").length;
            const pct = topics.length ? Math.round(((doneN + goodN) / topics.length) * 100) : 0;
            return (
              <div key={subject} className="card-ta overflow-hidden" data-testid={`topic-subject-${subject}`}>
                <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <span className="font-display font-bold text-slate-900 text-sm">{subject}</span>
                  <span className="text-[11px] font-semibold text-slate-500">
                    {doneN > 0 && <span className="text-emerald-600 mr-1.5">{doneN} bitirdi</span>}
                    {goodN > 0 && <span className="text-amber-600 mr-1.5">{goodN} iyi</span>}
                    %{pct}
                  </span>
                </div>
                <div className="p-3 flex flex-wrap gap-1.5">
                  {topics.map((t) => {
                    const st = statuses[k(subject, t)];
                    const meta = st ? STATUS_META[st] : null;
                    const key2 = k(subject, t);
                    return (
                      <button
                        key={t}
                        type="button"
                        disabled={readOnly || busyKey === key2}
                        onClick={() => toggle(subject, t)}
                        data-testid={`topic-cell-${subject}-${t}`}
                        title={readOnly ? (meta ? meta.label : t) : `${t} — tıkla: iyi / bitirdi / temizle`}
                        className={`text-[11px] px-2.5 py-1.5 rounded-lg border font-semibold transition-colors flex items-center gap-1 ${
                          meta ? meta.chip : "bg-white text-slate-600 border-slate-200 hover:border-indigo-400 hover:text-indigo-700"
                        } ${readOnly ? "cursor-default" : "cursor-pointer"}`}
                      >
                        {st === "good" && <Star size={11} />}
                        {st === "done" && <CheckCircle2 size={11} />}
                        {busyKey === key2 ? <Loader2 size={11} className="animate-spin" /> : null}
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
